import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FilePlus } from "lucide-react";
import { errorMessage, get } from "@/lib/api";
import { createNoteIn } from "@/features/crypto/createNote";
import { useIsUnlocked } from "@/features/crypto/keyring";
import { qk, useTree } from "@/lib/queries";
import { openNote, openView } from "@/lib/store";
import type { Workspace } from "@/lib/types";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KindIcon } from "@/features/notes/kinds";
import { cn } from "@/lib/utils";

interface Tpl {
  id: string;
  key: string;
  name: string;
  kind: string;
  description: string;
  isCustomized: boolean;
}

export function NewNoteDialog({ open, onOpenChange, workspace }: { open: boolean; onOpenChange(o: boolean): void; workspace: Workspace }) {
  const qc = useQueryClient();
  const tree = useTree(workspace.id);
  const templates = useQuery({
    queryKey: ["note-templates", workspace.id],
    queryFn: () => get<{ templates: Tpl[] }>(`/api/workspaces/${workspace.id}/note-templates`),
    enabled: open,
  });
  const [template, setTemplate] = useState<string>("");
  const [title, setTitle] = useState("");
  const [folderId, setFolderId] = useState("");
  const [busy, setBusy] = useState(false);
  const locked = !useIsUnlocked(workspace.encryptionScopeId) && !!workspace.encryptionScopeId;

  const folders = useMemo(() => {
    const list = tree.data?.folders ?? [];
    const out: { id: string; label: string }[] = [];
    const walk = (p: string | null, prefix: string) => {
      for (const f of list.filter((x) => x.parentId === p)) {
        out.push({ id: f.id, label: prefix + f.name });
        walk(f.id, `${prefix}${f.name} / `);
      }
    };
    walk(null, "");
    return out;
  }, [tree.data]);

  const create = async (tplKey: string) => {
    setBusy(true);
    try {
      const note = await createNoteIn(workspace, { folderId: folderId || null, title, templateKey: tplKey || null });
      await qc.invalidateQueries({ queryKey: qk.tree(workspace.id) });
      openNote(note.id, note.title, workspace.id);
      onOpenChange(false);
      setTitle("");
      setTemplate("");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>New note in “{workspace.name}”</DialogTitle>
          <DialogDescription>Start blank, or from an editable template. Template sections are optional — leave anything empty.</DialogDescription>
        </DialogHeader>
        {workspace.encryptionScopeId && (
          <p className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
            {locked ? "Unlock this project first: new notes are encrypted with its key." : "This project is end-to-end encrypted: the title and text are encrypted in your browser."}
          </p>
        )}
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void create(template);
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="nn-title">Title (optional)</Label>
              <Input id="nn-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Untitled" maxLength={300} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="nn-folder">Folder</Label>
              <select id="nn-folder" value={folderId} onChange={(e) => setFolderId(e.target.value)} className="w-full rounded-md border bg-background px-2 py-1.5 text-sm">
                <option value="">Top level (no folder)</option>
                {folders.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <fieldset>
            <legend className="mb-2 text-sm font-medium">Template</legend>
            <div className="grid gap-2 sm:grid-cols-2" role="radiogroup">
              <label className={cn("flex cursor-pointer items-start gap-2 rounded-md border p-2.5 text-sm hover:bg-accent", template === "" && "border-primary bg-accent")}>
                <input type="radio" name="tpl" className="mt-1" checked={template === ""} onChange={() => setTemplate("")} />
                <span>
                  <span className="flex items-center gap-1.5 font-medium">
                    <FilePlus className="size-4" aria-hidden /> Blank note
                  </span>
                  <span className="text-xs text-muted-foreground">An ordinary Markdown note.</span>
                </span>
              </label>
              {templates.data?.templates.map((t) => (
                <label key={t.id} className={cn("flex cursor-pointer items-start gap-2 rounded-md border p-2.5 text-sm hover:bg-accent", template === t.key && "border-primary bg-accent")}>
                  <input type="radio" name="tpl" className="mt-1" checked={template === t.key} onChange={() => setTemplate(t.key)} />
                  <span>
                    <span className="flex items-center gap-1.5 font-medium">
                      <KindIcon kind={t.kind} /> {t.name}
                      {t.isCustomized && <span className="text-xs font-normal text-muted-foreground">(customised)</span>}
                    </span>
                    <span className="text-xs text-muted-foreground">{t.description}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          <DialogFooter className="items-center gap-2 sm:justify-between">
            <Button
              type="button"
              variant="link"
              className="px-0"
              onClick={() => {
                onOpenChange(false);
                openView("settings", workspace.id, "Settings", { section: "templates" });
              }}
            >
              Edit templates…
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy || locked}>
                {busy ? "Creating…" : "Create note"}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
